"use client"

import clsx from "clsx"
import React, { JSX, memo } from "react"
import { BaseComponentProps } from "../../utils"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../shadcn/tooltip"

/**
 * @description 호버 툴팁. children 요소를 감싸서 사용. 마우스 올리면 말풍선 표시
 * @example
 * <TTooltip title="도움말 텍스트">
 *   <button>호버하세요</button>
 * </TTooltip>
 *
 * <TTooltip title="제목" description="상세 설명" side="bottom">
 *   <span>아래에 표시</span>
 * </TTooltip>
 */
interface TTooltipProps extends Omit<BaseComponentProps, "title"> {
	/** 툴팁 제목 텍스트 */
	title: React.ReactNode
	/** 툴팁 부가 설명 (제목 아래 표시) */
	description?: React.ReactNode
	/** 툴팁 내용에 마우스 올려도 유지 (링크 등 포함 시 true) */
	hoverableContent?: boolean
	/** 툴팁 비활성화 (children만 렌더링) */
	hiddenTooltip?: boolean
	/** 툴팁 표시 상태 제어. false면 강제 숨김, undefined면 hover 동작 */
	open?: boolean
	/** 제목 굵게 표시 */
	bold?: boolean
	/** 사각형 모서리 (rounded-lg) 적용 */
	isSquare?: boolean
	/**
	 * 툴팁 표시 방향
	 * @default "top"
	 */
	side?: "top" | "right" | "bottom" | "left"
	/**
	 * 툴팁 정렬
	 * @default "center"
	 */
	align?: "start" | "center" | "end"
	/** 방향 오프셋 (px) */
	sideOffset?: number
	/** 정렬 오프셋 (px) */
	alignOffset?: number
	/** 트리거 래퍼 div 에 추가할 className. 트리거 히트 영역을 children 보다 넓힐 때 사용 */
	triggerClassName?: string
	/** 툴팁 트리거 요소 (ReactElement 필수) */
	children: React.ReactElement
}

function TTooltipInner(props: TTooltipProps): JSX.Element {
	const {
		title,
		description,
		hoverableContent = false,
		hiddenTooltip = false,
		open,
		bold,
		isSquare = false,
		side = "top",
		align = "center",
		sideOffset = 0,
		alignOffset = 0,
		className = "",
		triggerClassName,
		children,
		// 부모가 asChild/Slot 으로 감싸 주입하는 props (onClick·onPointerEnter·ref·data-state 등) 를 트리거 요소까지 전달한다.
		// 흘려버리면 TTooltip 을 트리거로 쓰는 Radix 합성 (THoverMenu 의 HoverCardTrigger 등) 이 통째로 죽는다.
		...triggerProps
	} = props

	if (hiddenTooltip) {
		// 호출부가 children 그대로 반환되는 것에 의존한다 (GenerationFailureOverlay 등) — 래퍼를 끼우지 않는다.
		// 대신 이 경로에서는 triggerProps 를 전달할 곳이 없다. asChild 트리거로 쓰면서 hiddenTooltip 을 켜지 말 것.
		return <>{children}</>
	}

	return (
		<>
			<TooltipProvider>
				<Tooltip disableHoverableContent={!hoverableContent} open={open}>
					<TooltipTrigger asChild>
						<div {...triggerProps} className={clsx("flex items-center", triggerClassName)}>
							{children}
						</div>
					</TooltipTrigger>

					{open !== false && (
						<TooltipContent
							side={side}
							align={align}
							sideOffset={sideOffset}
							alignOffset={alignOffset}
							className={clsx(isSquare || description ? "rounded-lg" : "rounded-full", className)}
						>
							<div className={clsx("title", { "font-bold": bold || Boolean(description) })}>{title}</div>
							{description && <div className="description">{description}</div>}
						</TooltipContent>
					)}
				</Tooltip>
			</TooltipProvider>
		</>
	)
}

TTooltipInner.displayName = "TTooltip"

export const TTooltip = memo(TTooltipInner)
